import { createEntityAdapter, createSlice, type EntityState, type PayloadAction } from '@reduxjs/toolkit'

import { sharedHydrated } from '@/store/shared-actions'

import type { Notification } from '../types'

export const notificationsAdapter = createEntityAdapter<Notification>()
export type NotificationsState = EntityState<Notification, string>

/** Puts notifications into the state. One that is already there stays, and is read if either copy is read, so a read mark is never lost. */
function merge(state: NotificationsState, incoming: Notification[]) {
  for (const notification of incoming) {
    const have = state.entities[notification.id]
    if (!have) notificationsAdapter.addOne(state, notification)
    else if (!have.readAt && notification.readAt) have.readAt = notification.readAt
  }
}

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: notificationsAdapter.getInitialState(),
  reducers: {
    /** `addMany` ignores ids that already exist, so a replayed event can never create a duplicate notification. */
    notificationsAdded(state, action: PayloadAction<Notification[]>) {
      notificationsAdapter.addMany(state, action.payload)
    },
    /** Brings in notifications from storage (another tab's, or the saved ones): new ones are added, read marks are kept. */
    notificationsMerged(state, action: PayloadAction<Notification[]>) {
      merge(state, action.payload)
    },
    /** Marks notifications read. They are kept (history), never deleted. */
    notificationsRead(state, action: PayloadAction<{ ids: string[]; at: string }>) {
      for (const id of action.payload.ids) {
        const notification = state.entities[id]
        if (notification && !notification.readAt) notification.readAt = action.payload.at
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sharedHydrated, (state, action) => {
      merge(state, Object.values(action.payload.data.notifications.entities).filter((item): item is Notification => Boolean(item)))
    })
  },
})

export const { notificationsAdded, notificationsMerged, notificationsRead } = notificationsSlice.actions
